/**
 * Shared helpers for internal and custom model references used by item and zone editors.
 */
export const MODEL_CAPABILITY_OPTIONS = ['text', 'image', 'video', 'detect'];

/**
 * Custom providers exposed in model pickers and usage tips.
 */
export const CUSTOM_MODEL_PROVIDERS = [
    {
        provider: 'vllm',
        label: 'Custom vLLM model',
        placeholder: 'zai-org/GLM-OCR',
        tip: 'Paste the Hugging Face model path exactly as published there.',
    },
    {
        provider: 'ollama',
        label: 'Custom Ollama model',
        placeholder: 'glm-ocr:q8_0',
        tip: 'Use the Ollama model name and tag exactly as Ollama shows it.',
    },
];

export const CUSTOM_MODEL_OPTION_PREFIX = '__custom_provider__:';

const NUMERIC_METADATA_KEYS = new Set(['temperature', 'top_p', 'max_tokens', 'context_window']);

const DEFAULT_CUSTOM_MODEL_METADATA = {
    context_window: 8192,
    temperature: 0.2,
    top_p: 0.95,
    max_tokens: 1024,
};

/**
 * Builds the synthetic picker value used for provider-level custom options.
 */
export const buildCustomProviderOptionValue = (provider) => `${CUSTOM_MODEL_OPTION_PREFIX}${provider}`;

/**
 * Returns provider metadata for a custom provider identifier.
 */
export const getCustomProvider = (provider) => CUSTOM_MODEL_PROVIDERS.find((entry) => entry.provider === provider) || null;

/**
 * Detects whether a picker value points at a synthetic custom provider option.
 */
export const isCustomProviderOptionValue = (value) => typeof value === 'string' && value.startsWith(CUSTOM_MODEL_OPTION_PREFIX);

/**
 * Extracts the provider from a synthetic custom provider option.
 */
export const parseCustomProviderOptionValue = (value) => {
    if (!isCustomProviderOptionValue(value)) {
        return null;
    }

    return value.slice(CUSTOM_MODEL_OPTION_PREFIX.length).trim().toLowerCase() || null;
};

/**
 * Parses stored custom model values like `vllm:repo/model`.
 */
export const parseCustomModelValue = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    const separatorIndex = trimmed.indexOf(':');
    if (separatorIndex <= 0) {
        return null;
    }

    const provider = trimmed.slice(0, separatorIndex).trim().toLowerCase();
    if (!getCustomProvider(provider)) {
        return null;
    }

    return {
        provider,
        modelName: trimmed.slice(separatorIndex + 1).trim(),
    };
};

/**
 * Detects whether a stored model value is a custom provider-prefixed reference.
 */
export const isCustomModelValue = (value) => parseCustomModelValue(value) !== null;

/**
 * Builds the canonical provider-prefixed model value submitted to Laravel.
 */
export const buildCustomModelValue = (provider, modelName) => {
    const normalizedProvider = getCustomProvider(provider)?.provider;
    if (!normalizedProvider) {
        return '';
    }

    return `${normalizedProvider}:${(modelName || '').trim()}`;
};

/**
 * Returns default capabilities for internal models based on existing captioning behavior.
 */
export const getDefaultCapabilities = (model = null) => {
    if (model?.model_capabilities && typeof model.model_capabilities === 'object') {
        const storedCapabilities = model.model_capabilities;
        const fallback = model?.captioning_flag === 1 || model?.captioning_flag === true
            ? { in: ['image'], out: ['text'] }
            : { in: ['image'], out: ['detect'] };
        const normalizeList = (value, fallbackList) => {
            const list = Array.isArray(value) ? value : fallbackList;
            const normalized = list
                .map((entry) => String(entry || '').trim().toLowerCase())
                .filter((entry) => MODEL_CAPABILITY_OPTIONS.includes(entry));

            return Array.from(new Set(normalized.length ? normalized : fallbackList));
        };

        return {
            in: normalizeList(storedCapabilities.in, fallback.in),
            out: normalizeList(storedCapabilities.out, fallback.out),
        };
    }

    if (model?.captioning_flag === 1 || model?.captioning_flag === true) {
        return { in: ['image'], out: ['text'] };
    }

    return { in: ['image'], out: ['detect'] };
};

/**
 * Normalizes capability arrays to a stable, known list of values.
 */
export const normalizeCapabilities = (capabilities, model = null) => {
    const fallback = getDefaultCapabilities(model);
    const normalizeList = (value, fallbackList) => {
        const list = Array.isArray(value) ? value : fallbackList;
        const normalized = list
            .map((entry) => String(entry || '').trim().toLowerCase())
            .filter((entry) => MODEL_CAPABILITY_OPTIONS.includes(entry));

        return Array.from(new Set(normalized.length ? normalized : fallbackList));
    };

    return {
        in: normalizeList(capabilities?.in, fallback.in),
        out: normalizeList(capabilities?.out, fallback.out),
    };
};

/**
 * Strips empty metadata values and coerces supported numeric fields.
 */
export const sanitizeModelMetadata = (metadata = {}) => {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return {};
    }

    return Object.entries(metadata).reduce((carry, [key, rawValue]) => {
        if (rawValue === undefined || rawValue === null) {
            return carry;
        }

        if (Array.isArray(rawValue)) {
            const filtered = rawValue
                .map((entry) => String(entry || '').trim())
                .filter(Boolean);
            if (filtered.length) {
                carry[key] = filtered;
            }
            return carry;
        }

        const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
        if (value === '') {
            return carry;
        }

        if (NUMERIC_METADATA_KEYS.has(key)) {
            const numericValue = Number(value);
            if (!Number.isNaN(numericValue)) {
                carry[key] = key === 'max_tokens' || key === 'context_window'
                    ? Math.trunc(numericValue)
                    : numericValue;
            }
            return carry;
        }

        carry[key] = value;
        return carry;
    }, {});
};

/**
 * Returns the default metadata values shown for provider-backed custom models.
 */
export const getDefaultModelMetadata = () => ({ ...DEFAULT_CUSTOM_MODEL_METADATA });

/**
 * Produces a stable config object for model capabilities and metadata.
 */
export const normalizeModelConfig = (config = {}, model = null) => {
    return {
        model_capabilities: normalizeCapabilities(config?.model_capabilities, model),
        model_metadata: sanitizeModelMetadata(config?.model_metadata || {}),
    };
};

/**
 * Produces a stable config object for provider-backed custom models, including runtime defaults.
 */
export const normalizeCustomModelConfig = (config = {}, model = null) => {
    const existingMetadata = config?.model_metadata && typeof config.model_metadata === 'object' && !Array.isArray(config.model_metadata)
        ? config.model_metadata
        : {};

    return normalizeModelConfig({
        ...config,
        model_metadata: {
            ...getDefaultModelMetadata(),
            ...existingMetadata,
        },
    }, model);
};

/**
 * Derives legacy prompt/captioning/image flags so older listeners still react correctly.
 */
export const deriveLegacyModelFlags = (config = {}, model = null) => {
    const normalizedConfig = normalizeModelConfig(config, model);
    const inputCapabilities = normalizedConfig.model_capabilities.in;
    const outputCapabilities = normalizedConfig.model_capabilities.out;

    return {
        prompt: inputCapabilities.includes('text') ? 1 : 0,
        captioning: outputCapabilities.includes('text') ? 1 : 0,
        model_image_prompt: inputCapabilities.includes('image') ? 1 : 0,
    };
};

/**
 * Builds synthetic Autocomplete options for custom providers.
 */
export const buildCustomProviderOptions = () => {
    return CUSTOM_MODEL_PROVIDERS.map((entry) => ({
        label: entry.label,
        value: buildCustomProviderOptionValue(entry.provider),
        provider: entry.provider,
        isCustomProvider: true,
    }));
};

/**
 * Resolves the displayed picker option from a stored model value.
 */
export const resolveModelPickerOption = (modelValue, options = []) => {
    const internalOption = options.find((option) => option?.value === modelValue || option?.name === modelValue);
    if (internalOption) {
        return internalOption;
    }

    const customModel = parseCustomModelValue(modelValue);
    if (!customModel) {
        return null;
    }

    return options.find((option) => option?.provider === customModel.provider && option?.isCustomProvider) || null;
};
